import { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import type { ProductSize } from "../models/ProductSize";

interface DuplicateProductModalProps {
	open: boolean;
	product: ProductSize | null;
	existingProducts: ProductSize[];
	isDuplicating: boolean;
	onDuplicate: (name: string, sectionCode: string) => void;
	onCancel: () => void;
}

/** The copy carries the whole Mill Setup, so it needs its own name and section code. */
export const DuplicateProductModal = ({
	open,
	product,
	existingProducts,
	isDuplicating,
	onDuplicate,
	onCancel,
}: DuplicateProductModalProps) => {
	const [name, setName] = useState("");
	const [sectionCode, setSectionCode] = useState("");

	useEffect(() => {
		if (!open || !product) return;
		let candidate = "";
		for (let i = 1; i < 100; i++) {
			candidate = `${product.Name} (${i})`;
			if (!existingProducts.some((p) => p.Name === candidate)) break;
		}
		setName(candidate);
		setSectionCode("");
	}, [open, product, existingProducts]);

	if (!product) return null;

	const nameTaken = existingProducts.some(
		(p) => p.Name.toLowerCase() === name.trim().toLowerCase(),
	);
	const sectionTaken = existingProducts.some(
		(p) =>
			!!p.SectionCode &&
			p.SectionCode.toLowerCase() === sectionCode.trim().toLowerCase(),
	);
	const canDuplicate =
		!!name.trim() && !!sectionCode.trim() && !nameTaken && !sectionTaken;

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>DUPLICATE PRODUCT</DialogTitle>
			<DialogContent dividers>
				<Grid2 container spacing={1.5} sx={{ mt: 0.5 }}>
					<Grid2 size={{ xs: 12 }}>
						<Typography variant="body2" color="text.secondary">
							Source: <strong>{product.Name}</strong>
							{product.SectionCode ? ` - ${product.SectionCode}` : ""}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 7 }}>
						<TextField
							label="New Name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							error={nameTaken}
							helperText={nameTaken ? "This name already exists" : ""}
							fullWidth
							required
							size="small"
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 5 }}>
						<TextField
							label="New Section Code"
							value={sectionCode}
							onChange={(e) => setSectionCode(e.target.value)}
							error={sectionTaken}
							helperText={sectionTaken ? "This code already exists" : ""}
							fullWidth
							required
							size="small"
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
				</Grid2>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onCancel}
					disabled={isDuplicating}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={() => onDuplicate(name.trim(), sectionCode.trim())}
					disabled={!canDuplicate || isDuplicating}
				>
					{isDuplicating ? "DUPLICATING..." : "DUPLICATE"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
